import { For, Show, createMemo, createSignal, type Component } from 'solid-js';
import { Text } from '~ui/text';
import { formatPercent } from '~/lib/format';
import { candles } from '~data';
import { valueSeries, type Basis } from '~/stores/series-store';
import { Heading } from '~ui/text';
import { ArrowLeftIcon } from '~ui/icons';

// What each calendar year did, on all three bases.
//
// The chart above says what happened; this says when. A covered-call fund that
// is flat over five years is usually not five flat years — it is two good ones
// and three that gave it back, and a line that ends where it started hides
// exactly that.
//
// Each year is measured on its own rather than sliced out of the cumulative
// series. That matters for the cash basis: banked distributions do not compound,
// so the ratio of two points on a running total is not the return between them.
// Starting fresh at each year's first close, with one unit, gives the same
// arithmetic for all three columns.
//
// Diverging red/green, because zero is the meaningful middle here and the two
// directions are opposites rather than more and less of one thing. Every cell
// carries its number — a ramp is read to about three levels by eye, which is
// enough for "that was a bad year" and nowhere near enough for -4.2% vs -7.8%.

const COLUMNS: { basis: Basis; label: string; hint: string }[] = [
    { basis: 'nav', label: 'Price', hint: 'Unit price alone' },
    { basis: 'cash', label: '+ Cash', hint: 'Distributions banked as paid, never reinvested' },
    { basis: 'drip', label: '+ DRIP', hint: 'Distributions buy units at the close they are paid on' },
];

interface Period {
    label: string;
    returns: (number | null)[];
    /** The fund did not trade for the whole period — its first, or the current
        one. */
    partial: boolean;
    /** Only years drill down; a month has nothing under it. */
    year?: number;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * The return over one window, on each basis.
 *
 * Measured from the window's own first close with one unit rather than sliced
 * out of a running total — see the note above on why that is not optional for
 * the cash column.
 */
const returnsBetween = (symbol: string, from: number, to: number): (number | null)[] =>
    COLUMNS.map(({ basis }) => {
        const within = valueSeries(symbol, basis, from).filter((p) => p.t <= to);
        if (within.length < 2 || within[0].value <= 0) return null;
        return (within[within.length - 1].value / within[0].value - 1) * 100;
    });

export const YearlyReturns: Component<{ symbol: string }> = (props) => {
    // Which year is opened into months, or null for the year list. Local: it is
    // a way of looking at this panel, not a fact about the fund, and it should
    // reset when you move to another one.
    const [openYear, setOpenYear] = createSignal<number | null>(null);

    const bars = createMemo(() => candles(props.symbol));

    // Back to the year list whenever the fund changes — a month view of 2019 is
    // meaningless on a fund that listed in 2024.
    createMemo(() => {
        props.symbol;
        setOpenYear(null);
    });

    const rows = createMemo<Period[]>(() => {
        const all = bars();
        if (all.length === 0) return [];
        const traded = (from: string, to: string): boolean => all.some((c) => c.date >= from && c.date <= to);

        const year = openYear();
        if (year != null) {
            const out: Period[] = [];
            for (let m = 11; m >= 0; m -= 1) {
                const start = `${year}-${String(m + 1).padStart(2, '0')}-01`;
                const end = `${year}-${String(m + 1).padStart(2, '0')}-31`;
                if (!traded(start, end)) continue;
                out.push({
                    label: MONTHS[m],
                    returns: returnsBetween(props.symbol, Date.parse(start), Date.parse(end)),
                    // A month is partial if the fund was not trading in its
                    // first week or its last.
                    partial: !traded(start, `${year}-${String(m + 1).padStart(2, '0')}-07`) || !traded(`${year}-${String(m + 1).padStart(2, '0')}-24`, end),
                });
            }
            return out;
        }

        const first = Number(all[0].date.slice(0, 4));
        const last = Number(all[all.length - 1].date.slice(0, 4));
        const out: Period[] = [];
        for (let y = last; y >= first; y -= 1) {
            out.push({
                label: String(y),
                year: y,
                returns: returnsBetween(props.symbol, Date.parse(`${y}-01-01`), Date.parse(`${y}-12-31`)),
                // A year that traded from January to December on both ends. The
                // first and the current year almost never do, and a partial year
                // read as a full one is how a fund looks like it collapsed.
                partial: !traded(`${y}-01-01`, `${y}-01-15`) || !traded(`${y}-12-15`, `${y}-12-31`),
            });
        }
        return out;
    });

    /** The biggest move on the grid, so shading is relative to what this fund
        actually did rather than to a fixed band that leaves a quiet fund blank
        and a volatile one saturated throughout. */
    const scale = createMemo(() => {
        const all = rows().flatMap((r) => r.returns.filter((v): v is number => v != null).map(Math.abs));
        return all.length === 0 ? 1 : Math.max(...all, 1);
    });

    const fill = (value: number | null): string => {
        if (value == null || value === 0) return 'transparent';
        const weight = Math.min(Math.abs(value) / scale(), 1) * 0.75;
        return `color-mix(in srgb, var(--color-${value > 0 ? 'green' : 'red'}) ${(weight * 100).toFixed(1)}%, transparent)`;
    };

    return (
        <>
            {/* The header is the component's own, because it changes: it names
                the panel at the year level and the year you opened at the month
                level, and a caption held by the parent could not say the second
                thing. */}
            <div class='flex h-row shrink-0 items-center gap-1 border-b bg-muted px-2'>
                <Show
                    when={openYear() != null}
                    fallback={
                        <Heading level={2}>By year</Heading>
                    }>
                    <button type='button' class='flex cursor-default items-center gap-1 rounded px-1 hover:bg-muted-foreground/20' title='Back to years' onClick={() => setOpenYear(null)}>
                        <ArrowLeftIcon />
                        <Heading level={2}>{`${openYear()} · by month`}</Heading>
                    </button>
                </Show>
            </div>

        <Show when={rows().length > 0} fallback={<div class='flex min-h-0 flex-1 items-center justify-center px-2'><Text tone='muted'>No price history</Text></div>}>
            <div class='min-h-0 flex-1 overflow-auto scrollbar-hide'>
                <table class='w-full table-fixed border-collapse'>
                    <thead>
                        <tr>
                            <th class='h-row w-14 border-b px-1 text-left text-sm font-normal'>
                                <Text tone='muted'>{openYear() == null ? 'Year' : 'Month'}</Text>
                            </th>
                            <For each={COLUMNS}>
                                {(c) => (
                                    <th class='h-row border-b border-l px-1 text-center text-sm font-normal' title={c.hint}>
                                        <Text tone='muted'>{c.label}</Text>
                                    </th>
                                )}
                            </For>
                        </tr>
                    </thead>
                    <tbody>
                        <For each={rows()}>
                            {(row) => (
                                // A year opens into its months; a month has
                                // nothing under it and stays inert rather than
                                // offering a click that does nothing.
                                <tr
                                    class={row.year == null ? '' : 'cursor-default hover:bg-muted'}
                                    onClick={row.year == null ? undefined : () => setOpenYear(row.year!)}>
                                    <td class='h-row border-b px-1 text-sm'>
                                        <span class='flex items-baseline gap-1'>
                                            <Text weight='medium'>{row.label}</Text>
                                            {/* Marked, not hidden: a stub of a period
                                                is a real number about a real span,
                                                and dropping it loses the year a fund
                                                listed. */}
                                            <Show when={row.partial}>
                                                <Text tone='muted'>·</Text>
                                            </Show>
                                        </span>
                                    </td>
                                    <For each={row.returns}>
                                        {(value) => (
                                            <td class='h-row border-b border-l p-0 text-center' style={{ 'background-color': fill(value) }}>
                                                <span class='text-sm'>{value == null ? <Text tone='muted'>—</Text> : formatPercent(value, 1)}</span>
                                            </td>
                                        )}
                                    </For>
                                </tr>
                            )}
                        </For>
                    </tbody>
                </table>
            </div>

            <div class='flex h-row shrink-0 items-center border-t px-2'>
                <Text tone='muted' class='truncate'>
                    {openYear() == null ? 'Calendar year · click one for months' : 'Calendar month'} · · marks a partial one
                </Text>
            </div>
        </Show>
        </>
    );
};

export default YearlyReturns;
